import React, { useState, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import NavigationBar from '../../components/layout/Navbar';
import { useCalendario, usePlanOperativo } from './hooks/usePlanOperativo';
import ModalDia from './components/ModalDia';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
               'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const formatearFechaCalendario = (date) => {
  const anio = date.getFullYear();
  const mes = String(date.getMonth() + 1).padStart(2, '0');
  const dia = String(date.getDate()).padStart(2, '0');
  return `${anio}-${mes}-${dia}`;
};

export default function PlanOperativo() {
  const hoy = new Date();
  const [anio,     setAnio]    = useState(hoy.getFullYear());
  const [mes,      setMes]     = useState(hoy.getMonth() + 1);
  const [fechaSel, setFechaSel] = useState(null);
  const [modalOpen, setModal]  = useState(false);

  const { secciones, actividades, contexto, error: errorBase } = usePlanOperativo();
  const { diasConRegistros, planMes, recargar, guardarPlan, eliminarPlan, cargando } = useCalendario(anio, mes);

  const esAdmin   = contexto?.esAdmin;
  const nombreFinca = esAdmin
    ? 'Vista consolidada — todas las fincas'
    : (contexto?.finca?.nombre || 'Plan Operativo');

  const eventos = useMemo(() => {
    const evts = [];

    planMes.forEach(p => {
      const titulo = esAdmin
        ? `📋 ${p.finca_nombre} · ${p.actividad_nombre} (${p.jornales_planeados}j)`
        : `📋 ${p.actividad_nombre} (${p.jornales_planeados}j)`;
      evts.push({
        id:    `plan-${p.id}`,
        title: titulo,
        date:  p.fecha.slice(0, 10),
        color: p.color_hex,
        textColor: '#fff',
        extendedProps: { tipo: 'plan', data: p },
      });
    });

    Object.entries(diasConRegistros).forEach(([fecha, info]) => {
      evts.push({
        id:    `reg-${fecha}`,
        title: `✅ ${info.total_jornales} jornales`,
        date:  fecha.slice(0, 10),
        color: '#1D9E75',
        textColor: '#fff',
        extendedProps: { tipo: 'registro' },
      });
    });

    return evts;
  }, [planMes, diasConRegistros, esAdmin]);

  const abrirDia = (info) => {
    const fecha = info.dateStr || info.event.startStr;
    setFechaSel(fecha);
    setModal(true);
  };

  const cambiarMes = (delta) => {
    let nm = mes + delta;
    let na = anio;
    if (nm > 12) { nm = 1;  na++; }
    if (nm < 1)  { nm = 12; na--; }
    setMes(nm);
    setAnio(na);
  };

  const renderDiaCalendario = (arg) => {
    const fechaKey = formatearFechaCalendario(arg.date);
    const info = diasConRegistros[fechaKey];
    const tieneRegistros = Boolean(info);

    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 42 }}>
        <div>{arg.dayNumberText}</div>
        {tieneRegistros && (
          <span
            title={`Hay registros: ${info?.total_jornales ?? 0} jornales`}
            style={{
              position: 'absolute',
              right: 4,
              bottom: 4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#1D9E75',
              boxShadow: '0 0 0 2px rgba(29, 158, 117, 0.16)',
            }}
          />
        )}
      </div>
    );
  };

  return (
    <>
    <NavigationBar />
    <main style={{ fontFamily: 'sans-serif', padding: '1rem', maxWidth: 960, margin: '0 auto', width: '100%' }}>

      <div style={styles.header}>
        <div style={styles.logoBox}>🌿</div>
        <div>
          <h1 style={styles.h1}>Plan Operativo — {nombreFinca}</h1>
          <p style={styles.sub}>Plan de trabajo agrícola {anio}</p>
        </div>
      </div>

      <div style={styles.navRow}>
        <button style={styles.navBtn} onClick={() => cambiarMes(-1)}>‹</button>
        <span style={styles.mesTitulo}>{MESES[mes - 1]} {anio}</span>
        <button style={styles.navBtn} onClick={() => cambiarMes(1)}>›</button>
      </div>

      <div style={styles.leyenda}>
        {[
          { color: '#1D9E75', label: 'Cosecha' },
          { color: '#185FA5', label: 'Fumigación' },
          { color: '#BA7517', label: 'Fertilización' },
          { color: '#993556', label: 'Poda / Pintura' },
          { color: '#639922', label: 'Trampas' },
          { color: '#888780', label: 'Infraestructura' },
        ].map(l => (
          <span key={l.color} style={styles.leyItem}>
            <span style={{ ...styles.leyDot, background: l.color }} />
            {l.label}
          </span>
        ))}
      </div>

      {errorBase && (
        <div style={styles.errorBox}>
          ⚠️ Error al conectar con el servidor: {errorBase}
        </div>
      )}

      {cargando && (
        <div style={styles.loadingBox}>Cargando calendario...</div>
      )}

      <div style={styles.calWrap}>
        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={esLocale}
          initialDate={`${anio}-${String(mes).padStart(2, '0')}-01`}
          key={`${anio}-${mes}`}
          headerToolbar={false}
          events={eventos}
          dateClick={abrirDia}
          eventClick={abrirDia}
          dayCellContent={renderDiaCalendario}
          height="auto"
          dayMaxEvents={3}
          eventDisplay="block"
        />
      </div>

      {modalOpen && (
        <ModalDia
          fecha={fechaSel}
          secciones={secciones}
          actividades={actividades}
          esAdmin={esAdmin}
          fincas={contexto?.fincas}
          planMes={planMes}
          guardarPlan={guardarPlan}
          eliminarPlan={eliminarPlan}
          onClose={() => { setModal(false); recargar(); }}
        />
      )}
    </main>
    </>
  );
}

const styles = {
  header: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1rem' },
  logoBox: {
    width: 44, height: 44, fontSize: 24,
    background: '#E1F5EE', borderRadius: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  h1:        { margin: 0, fontSize: 18, fontWeight: 600, color: '#0F6E56' },
  sub:       { margin: 0, fontSize: 13, color: '#666' },
  navRow:    { display: 'flex', alignItems: 'center', gap: 12, marginBottom: '0.75rem' },
  navBtn:    {
    width: 32, height: 32, border: '1px solid #ccc',
    borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 18,
  },
  mesTitulo: { fontSize: 17, fontWeight: 600, minWidth: 180, textAlign: 'center' },
  leyenda:   { display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: '0.75rem' },
  leyItem:   { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#555' },
  leyDot:    { width: 10, height: 10, borderRadius: 3, display: 'inline-block' },
  calWrap:     { background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5', overflow: 'hidden' },
  errorBox:    {
    background: '#FFF3CD', border: '1px solid #FBBF24', borderRadius: 8,
    padding: '10px 14px', marginBottom: '0.75rem', fontSize: 13, color: '#92400E',
  },
  loadingBox:  { textAlign: 'center', padding: '0.5rem', fontSize: 13, color: '#888', marginBottom: '0.5rem' },
};
